import { COUNTRIES, DATA_CENTERS, DEFAULT_LOCALE, DEFAULT_LOCALE_CODE, LOCALES } from 'i18n/constants';
import { CountryCode, DataCenter, DataCenterCode, LanguageCode, Locale, LocaleCode } from 'i18n/types';

export const DEV = /^(stg|dev|sandbox|qa|demo)/;
const DEV_HOST = /.*(firebaseapp\.com|ngrok\.io|netlify\.app)/;

/**
 * Is a Dev based location (stg1, dev1, firebase, etc)
 */
const isDev = ({ hostname }: Location = window.location) => process.env.NODE_ENV === 'development' || DEV_HOST.test(hostname) || DEV.test(hostname.split('.')[0]);

export function getDataCenter({ host }: Location = window.location): DataCenter {
  if (!isDev()) {
    return Object.values(DATA_CENTERS).find(dc => dc.prodHost === host) || DATA_CENTERS.NA;
  }

  return Object.values(DATA_CENTERS).find(dc => dc.devHost === host) || DATA_CENTERS.NA;
}

function isValidInDataCenter(locale: Locale | string, dc: DataCenterCode | DataCenter = getDataCenter()): boolean {
  if (!dc) return false;

  const dataCenterCode = typeof dc === 'string' ? dc : dc.code;
  const country = typeof locale === 'string' ? LOCALES[locale]?.country : locale.country;

  return country?.dataCenter.code === dataCenterCode;
}

function isDataCenterCode(maybeCode: DataCenterCode | DataCenter): maybeCode is DataCenterCode {
  return typeof maybeCode === 'string' && Object.values(DataCenterCode).includes(maybeCode);
}

/**
 * Gets all locales within the given data center
 */
export function getLocalesForDataCenter(dc: DataCenterCode | DataCenter = getDataCenter()): Locale[] {
  const dataCenterCode = isDataCenterCode(dc) ? dc : dc.code;

  return Object.values(LOCALES).filter(locale => locale.country.dataCenter.code === dataCenterCode);
}

/**
 * Given a data center and language will try and determine the best default locale.
 */
export function getDefaultLocale(lang: LanguageCode = LanguageCode.ENGLISH, dc: DataCenter | DataCenterCode = getDataCenter()): Locale {
  const dataCenterCode = typeof dc === 'string' ? dc : dc.code;
  const validLocales = getLocalesForDataCenter(dataCenterCode);
  const isDefaultDataCenter = isValidInDataCenter(DEFAULT_LOCALE_CODE, dc);

  // Try to prioritize DEFAULT_LOCALE if it is the same language
  if (isDefaultDataCenter && DEFAULT_LOCALE_CODE.startsWith(lang)) {
    return DEFAULT_LOCALE;
  }

  // Get the first locale that includes this language
  const locale: Locale | undefined = validLocales.find(validLocale => validLocale.code.startsWith(lang.toLowerCase()));

  return locale || validLocales[0] || DEFAULT_LOCALE;
}

export const getLocale = (localeCode: LocaleCode): Locale => {
  return LOCALES[localeCode] || getDefaultLocale();
};

export function getLanguageCode(locale: Locale | LanguageCode | string): LanguageCode {
  if (typeof locale === 'string' && Object.values(LanguageCode).includes(locale as LanguageCode)) {
    return locale as LanguageCode;
  }

  return typeof locale === 'string' ? LOCALES[locale]?.languageCode : locale?.languageCode;
}

export function getCountryCode(locale: Locale | LanguageCode | string): CountryCode {
  if (typeof locale === 'string' && Object.values(LanguageCode).includes(locale as LanguageCode)) {
    // If it is JUST a language
    if (locale !== 'en' && process.env.NODE_ENV === 'development') console.error(`Unable to retrieve the country from the language code '${locale}'`);

    return COUNTRIES.US.code;
  }

  const country = typeof locale === 'string' ? LOCALES[locale]?.country : locale?.country;

  return country?.code;
}
